/**
 * gdrive_convert - Convert a Google Drive file to a native River entity
 *
 * Google Docs  → Note  (editable markdown)
 * Google Sheets → Sheet (editable cells)
 * Other files  → External.GoogleDrive.* reference entity
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { googleDriveAdapter } from '../../services/integrations/adapters/GoogleDriveAdapter';
import { GoogleDriveErrorHandler } from '../../services/integrations/adapters/GoogleDriveErrorHandler';
import { useSpaceStore } from '../../stores/spaceStore';

export const gdrive_convert: SimpleDocCommand = {
  id: 'gdrive_convert',
  logicalName: 'gdrive_convert',
  version: 'v1',

  description: `Convert a Google Drive file to a native, editable River entity.

**PREREQUISITES:**
- Google Drive must be connected
- File ID must be obtained from gdrive_search

**WHAT IT DOES:**
1. Fetches the file from Google Drive
2. Converts to a native River entity:
   - Google Docs  → Note  (fully editable, markdown content)
   - Google Sheets → Sheet (fully editable, live cells)
   - PDFs / images / other → Drive reference entity (viewable)
3. Places the entity in the current (or specified) space
4. Re-converting the same file returns the existing entity (deduplication)

**DIFFERENCE FROM gdrive_import:**
- gdrive_import creates a Drive-linked viewer (iframe, not editable)
- gdrive_convert creates an independent, fully editable River entity with the actual content

**PARAMETERS:**
- fileId: Google Drive file ID (from gdrive_search)
- spaceId: Optional target space ID (defaults to current space)

**EXAMPLES:**

Convert a Google Doc to an editable Note:
{ "fileId": "1abc123xyz..." }

Convert a Google Sheet into a specific space:
{ "fileId": "1def456uvw...", "spaceId": "space_12345" }

**CONVERSION MAPPING:**
- Google Doc  → Note  (fully editable)
- Google Sheet → Sheet (fully editable)
- PDF         → Drive reference (viewable)
- Image       → Drive reference (viewable)
- Other       → Drive reference (viewable)

**LIMITS:**
- Max file size: 50MB (for non-Workspace files)`,

  schema: {
    parameters: [
      {
        name: 'fileId',
        type: 'string',
        description: 'Google Drive file ID (from gdrive_search results)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim() === '') {
            return 'File ID is required';
          }
          return null;
        }
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Target space ID (defaults to current space)',
        required: false
      }
    ]
  },

  formatViewData: (result: StandardToolResult) => {
    const d = result?.data;
    return {
      type: 'gdrive_convert',
      summary: result?.message || 'Conversion completed',
      message: result?.message,
      data: {
        entityId: d?.entityId,
        entityType: d?.entityType,
        conversionType: d?.conversionType,
        fileName: d?.fileName,
        fileId: d?.fileId,
        spaceId: d?.spaceId
      }
    };
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { fileId, spaceId } = params;

    console.log('[gdrive_convert] Converting file:', fileId);

    try {
      const { default: useEntityStore } = await import('../../stores/entityStore');
      const entities = useEntityStore.getState().entities;

      const driveIntegrations = entities.filter((e: any) =>
        (e.entityType === 'Integration.GoogleDrive' || e.entityType === 'Integration') &&
        e.skeleton?.integrationType === 'GoogleDrive' &&
        e.skeleton?.status === 'connected' &&
        !e.trashed
      );

      if (driveIntegrations.length === 0) {
        return {
          status: 'error',
          message: 'Google Drive not connected',
          error: { code: 'not_connected', category: 'validation', retryable: false },
          format: 'plaintext'
        };
      }

      const driveIntegration = driveIntegrations.sort((a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];

      const targetSpaceId = spaceId || useSpaceStore.getState().activeSpace?.id;

      if (!targetSpaceId) {
        return {
          status: 'error',
          message: 'No active space found. Please specify a spaceId or ensure a space is open.',
          error: { code: 'no_active_space', category: 'validation', retryable: false },
          format: 'plaintext'
        };
      }

      const result = await googleDriveAdapter.convertFile(
        driveIntegration._id,
        fileId,
        targetSpaceId
      );

      console.log('[gdrive_convert] Successfully converted:', result.entityId, `(${result.conversionType})`);

      return {
        status: 'success',
        message: result.message,
        data: {
          entityId: result.entityId,
          entityType: result.entityType,
          conversionType: result.conversionType,
          fileName: result.fileName,
          fileId: fileId,
          spaceId: targetSpaceId
        },
        format: 'json'
      };

    } catch (error: any) {
      const errorInfo = GoogleDriveErrorHandler.classifyError(error);

      const categoryMap: Record<string, 'validation' | 'execution' | 'timeout' | 'permission' | 'system' | 'cancelled'> = {
        'configuration': 'validation',
        'network': 'execution',
        'api': 'execution',
        'auth': 'permission',
        'validation': 'validation'
      };

      const standardCategory = categoryMap[errorInfo.category] || 'execution';

      console.error('[gdrive_convert] Error:', {
        code: errorInfo.errorCode,
        category: errorInfo.category,
        standardCategory,
        retryable: errorInfo.retryable,
        originalError: error
      });

      return {
        status: 'error',
        message: errorInfo.userMessage,
        error: {
          code: errorInfo.errorCode,
          category: standardCategory,
          retryable: errorInfo.retryable,
          details: errorInfo.details
        },
        format: 'plaintext'
      };
    }
  }
};
